import { createSlice } from "@reduxjs/toolkit";
import { Call, Device } from '@twilio/voice-sdk';
import { CallProps, PhoneBurnerObject, TwilioCall } from "../../models/TwilioCall";
import {
  IncomingMessage,
  TwilioConversation,
} from "../../models/TwilioConversation";
import {
  callNextPhoneInBurner,
  cleanPhoneBurner,
  createConversation,
  fetchCalls,
  fetchChatToken,
  fetchConversationMessages,
  fetchConversations,
  fetchPhoneContact,
  fetchVoiceToken,
  finishTranferConference,
  handleIncomingMessage,
  health,
  joinConference,
  markMessageAsRead,
  removeCurrentConversation,
  removePhoneFromBurner,
  sendMessage,
  setConferenceId,
  setPhoneBurner,
  setStateLeadCreated,
  setTwilioDevice,
  setTwilioDeviceState,
  startConferenceFromCall,
  startNewConference,
  startTranferConference,
} from "./actions";
import { GenericError } from "../../models/GenericError";

export interface TwilioState {
  isLoading: boolean;
  voiceToken: string | null;
  chatToken: string;
  error: GenericError | null;
  conversations: TwilioConversation[];
  calls: CallProps[];
  currentConversation: TwilioConversation | null;
  unreadMessages?: IncomingMessage[];
  twilioDeviceState: Device.State;
  device: Device | null;
  currentCall: Call | null;
  areTwilioCredentialsValid?: boolean;
  phoneContact?: any,
  conferenceId?: string;
  transferConferenceId?: string;
  transferTo?: string
  phoneBurner?: PhoneBurnerObject | undefined;
  isLeadCreated?: boolean;
  inboundPageToken?: string;
  outboundPageToken?: string;
  conversationPageSize: number
}

const initialState: TwilioState = {
  isLoading: false,
  voiceToken: null,
  chatToken: "",
  error: null,
  calls: [],
  currentConversation: null,
  unreadMessages: [],
  twilioDeviceState: Device.State.Unregistered,
  device: null,
  currentCall: null,
  conferenceId: "",
  transferConferenceId: "",
  transferTo: undefined,
  inboundPageToken: undefined,
  outboundPageToken: undefined,
  conversations: [],
  conversationPageSize: 10,
  phoneContact: null
};

// slice
const twilioSlice = createSlice({
  name: "twilio",
  initialState: initialState,
  reducers: {
      clearCalls(state) {
      state.calls = [];
      },
    },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVoiceToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchVoiceToken.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.voiceToken = action.payload;
      })
      .addCase(fetchVoiceToken.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchChatToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchChatToken.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.chatToken = action.payload;
      })
      .addCase(fetchChatToken.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPhoneContact.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPhoneContact.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.phoneContact = action.payload;
      })
      .addCase(fetchPhoneContact.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchConversations.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCalls.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCalls.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.calls = [...state.calls, ...action.payload.calls];
        state.outboundPageToken = action.payload.nextOutboundPageToken;
        state.inboundPageToken = action.payload.nextInboundPageToken;
      })
      .addCase(fetchCalls.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
        state.calls = [];
      })
      .addCase(createConversation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createConversation.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.conversations = state.conversations.concat(action.payload);
        state.currentConversation = action.payload;
      })
      .addCase(createConversation.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendMessage.fulfilled, (state, action: any) => {
        state.isLoading = false;
        if (state.currentConversation) {
          const updatedConversation = state.currentConversation;
          const updatedConversationMessages =
            state.currentConversation?.messages?.concat(action.payload) ?? [
              action.payload,
            ];
          updatedConversation.messages = updatedConversationMessages;
          state.currentConversation = updatedConversation;
          const currentAllConversations = state.conversations;
          const newMessageConversation = state.conversations?.find(
            (x) => x.sid === action.payload.conversationSid
          );
          if (newMessageConversation) {
            const index = state.conversations?.indexOf(newMessageConversation);
            if (currentAllConversations && index !== -1) {
              currentAllConversations[index] = {
                ...newMessageConversation,
                messages: updatedConversationMessages,
                lastMessage: action.payload.body ?? "",
                dateUpdated: action.payload.dateCreated,
              };
              state.conversations = currentAllConversations;
            }
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(handleIncomingMessage.fulfilled, (state, action: any) => {
        const currentAllConversations = state.conversations;
        let newMessageConversation = state.conversations?.find(
          (x) => x.sid === action.payload.conversationSid
        );
        if (newMessageConversation) {
          const index = state.conversations?.indexOf(newMessageConversation);
          if (currentAllConversations && index !== -1) {
            currentAllConversations[index] = {
              ...newMessageConversation,
              newMessage: true,
              lastMessage: action.payload.text ?? "",
              dateUpdated: action.payload.date,
            };
            state.conversations = currentAllConversations;
          }
        } else {
          newMessageConversation = state.conversations?.find(
            (x) => x.sid === action.payload.conversationSid
          );
          if (newMessageConversation) {
            const index = state.conversations?.indexOf(newMessageConversation);
            if (currentAllConversations && index !== -1) {
              currentAllConversations[index] = {
                ...newMessageConversation,
                newMessage: true,
                lastMessage: action.payload.text ?? "",
                dateUpdated: action.payload.date,
              };
              state.conversations = currentAllConversations;
            }
          } else {
            state.conversations = state.conversations.concat({
              friendlyName: action.payload.from,
              participant: action.payload.from,
              dateUpdated: action.payload.date,
              sid: action.payload.conversationSid,
              lastMessage: action.payload.text,
            } as TwilioConversation);
          }
        }
        if (state.currentConversation?.sid === action.payload.conversationSid) {
          const conversation = state.currentConversation;
          if (conversation) {
            conversation.messages = conversation.messages?.concat({
              body: action.payload.text,
              dateCreated: action.payload.date,
              author: action.payload.from,
              sid: action.payload.conversationSid,
            });
            state.currentConversation = conversation;
          }
        } else {
          state.unreadMessages = state.unreadMessages?.concat(action.payload);
        }
      })
      .addCase(markMessageAsRead.fulfilled, (state, action: any) => {
        state.unreadMessages = state.unreadMessages?.filter(
          (x) => x.conversationSid !== action.payload.conversationSid
        );
      })
      .addCase(fetchConversationMessages.fulfilled, (state, action: any) => {
        state.unreadMessages = state.unreadMessages?.filter(
          (x) => x.conversationSid !== action.payload.sid
        );
        const conversation = state.conversations.find(
          (x) => x.sid === action.payload.sid
        );
        if (conversation) {
          conversation.messages = action.payload.messages;
          state.currentConversation = conversation;
        }
      })
      .addCase(setTwilioDeviceState.fulfilled, (state, action: any) => {
        state.twilioDeviceState = action.payload;
        if (action.payload === Device.State.Registered) {
          state.conferenceId = undefined;
        }
      })
      .addCase(setTwilioDevice.fulfilled, (state, action: any) => {
        state.device = action.payload;
      })
      .addCase(health.fulfilled, (state, action: any) => {
        state.areTwilioCredentialsValid = action.payload;
      })
      .addCase(removeCurrentConversation.fulfilled, (state, action: any) => {
        state.currentConversation = null;
      })
      .addCase(startTranferConference.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startTranferConference.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.transferConferenceId = action.payload.client_conference_id;
        console.log(action)
        state.transferTo = action.payload.transferTo
      })
      .addCase(startTranferConference.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(finishTranferConference.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(finishTranferConference.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.transferConferenceId = undefined;
        state.transferTo = undefined
      })
      .addCase(finishTranferConference.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(joinConference.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(joinConference.fulfilled, (state, action: any) => {
        state.isLoading = false;
      })
      .addCase(joinConference.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(startConferenceFromCall.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startConferenceFromCall.fulfilled, (state, action: any) => {
        state.conferenceId = action.payload.client_conference_id;
        state.isLoading = false;
      })
      .addCase(startConferenceFromCall.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(setPhoneBurner.fulfilled, (state, action: any) => {
        state.phoneBurner = {
          remainingList: action.payload,
        };
      })
      .addCase(callNextPhoneInBurner.fulfilled, (state, action: any) => {
        if (state.phoneBurner) {
          state.phoneBurner = {
            currentCall: state.phoneBurner.remainingList[0],
            remainingList: state.phoneBurner.remainingList.slice(1),
          };
        }
      })
      .addCase(removePhoneFromBurner.fulfilled, (state, action: any) => {
        if (state.phoneBurner) {
          state.phoneBurner = {
            currentCall: state.phoneBurner.currentCall,
            remainingList: state.phoneBurner.remainingList.filter(
              (_, index) => index !== action.payload
            ),
          };
        }
      })
      .addCase(cleanPhoneBurner.fulfilled, (state, action: any) => {
        if (state.phoneBurner) {
          state.phoneBurner = {
            currentCall: undefined,
            remainingList: [],
          };
        }
      })
      .addCase(setStateLeadCreated.fulfilled, (state, action: any) => {
        state.isLeadCreated = action.payload;
      })
      .addCase(startNewConference.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startNewConference.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.conferenceId = action.payload.conference_id;
      })
      .addCase(startNewConference.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(setConferenceId.fulfilled, (state, action: any) => {
        state.conferenceId = action.payload
      });

  },
});

export default twilioSlice.reducer;
export const { clearCalls } = twilioSlice.actions;
